import Input from "components/input"
import Select from "components/select";
import { SwitchButton } from "components/switchButton"
import { z } from "zod";
import { Link, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { ICreateOrgAccountForm, IOrgEventIdsItem, IOrgIdsItem, IOrgRoleIdsItem } from "interfaces";
import { zodResolver } from "@hookform/resolvers/zod";
import { HttpError, useCreate, useOne } from "@refinedev/core";
import toast from "react-hot-toast";
import { Label } from "components/label";
import Button from "components/button";
import { PHONE_CODE, REGEX_PHONE_NUMBER, REGEX_VALID_EMAIL } from "utills/constants";
import { FullScreenLoading } from "components/fullScreenLoading";

const CreateOrgAccountSchema = z.object({
  is_active: z.boolean(),
  organization_id: z.string().min(1, "This field is required"),
  role_id: z.string().min(1, "This field is required"),
  email: z
    .string()
    .min(1, { message: "This field is required" })
    .refine((value) => 
      REGEX_VALID_EMAIL.test(value ?? ""), 
      'Invalid email address'
    ),
  first_name: z.string().min(1, "This field is required").max(50, "Text length is too long"),
  last_name: z.string().min(1, "This field is required").max(50, "Text length is too long"),
  event_ids: z.array(z.string()).min(1, "This field is required"),
  phone_area_code: z.string(),
  phone: z.union([
    z.literal(""),
    z.string().refine((value) => 
      REGEX_PHONE_NUMBER.test(value ?? ""), 
      'Invalid phone number'
    )
  ]),
});

const EVENT_ALL_OPTION = {label: "All", value: "all"};

export const CreateAccountForm: React.FC = () => {

  const navigate = useNavigate();
  const { control, resetField, register, handleSubmit, watch, formState: { errors } } = useForm<ICreateOrgAccountForm, HttpError, ICreateOrgAccountForm>({resolver: zodResolver(CreateOrgAccountSchema)});

  const { mutate, isLoading: isLoading } = useCreate();

  const onSubmit = (values: ICreateOrgAccountForm) => {
    const submitValues = {...values};

    mutate({
      resource: "organization/account",
      meta: {
        ignoreResourceWrapper: true
      },
      values: {
        ...submitValues
      },
    },
    {
      onSuccess: () => {
        toast.success("Create successfully");
        navigate("/organization/accounts");
      },
    });
  }

  const { data: organizationIdsData, isLoading: isLoadingOrganizationIds } = useOne<IOrgIdsItem[], HttpError>({
    resource: "organization/ids",
    id: "",
    meta: {
      resource: "data"
    }
  });

  const { data: organizationRolesData, isFetching: isFetchingOrganizationRoles } = useOne<IOrgRoleIdsItem[], HttpError>({
    resource: "organization",
    id: `${watch("organization_id")}/roles`,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!watch("organization_id"),
    } as any,
  });

  const { data: organizationEventsData, isFetching: isFetchingOrganizationEvents } = useOne<IOrgEventIdsItem[], HttpError>({
    resource: "organization",
    id: `${watch("organization_id")}/events`,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!watch("organization_id"),
    } as any,
  });

  const organizationIds = (organizationIdsData?.data ?? []).map((org: IOrgIdsItem) => (
    { value: org.id, label: org.name_en }
  ));

  const organizationRolesIds = (organizationRolesData?.data ?? []).map((role: IOrgRoleIdsItem) => (
    { value: role.id, label: role.name }
  ));

  const organizationEventIds = ((organizationEventsData?.data ?? []).length > 0 ? [EVENT_ALL_OPTION] : []).concat((organizationEventsData?.data ?? []).map((event: IOrgEventIdsItem) => (
    { value: event.id, label: event.name_en }
  )));

  return (
    <form className="w-full flex flex-col gay-y-2 sm:gap-y-4" onSubmit={handleSubmit(onSubmit)}>
      <div className="box-container">
        <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6">
          <div className="sm:col-span-2">
            <Label 
              required
              id="is_active" 
              name="Status"  
            />
            <Controller
              control={control}
              name="is_active"
              defaultValue={true}
              render={({ field: { onChange, value = true, ref } }) => (
                <SwitchButton checked={value} onChange={onChange} />
              )}
            />
          </div>
          <div className="sm:col-span-4">
            <Label 
              required
              id="organization_id" 
              name="Org name"  
            />
            <Controller
              control={control}
              name="organization_id"
              defaultValue=""
              render={({ field: { onChange, value = "", ref } }) => (
                <Select 
                  inputRef={ref}
                  options={organizationIds}
                  id="organization_id"
                  placeholder="Select Organization"
                  value={organizationIds.find((selected: {label: string; value: string}) => selected.value === value)}
                  onChange={(selected: {label: string; value: string}) => {
                    onChange(selected?.value);
                    resetField("role_id", {defaultValue: ""});
                    resetField("event_ids", {defaultValue: []});
                  }}
                  error={errors["organization_id"]}
                  isLoading={isLoadingOrganizationIds}
                />
              )}
            />
            {errors["organization_id"] ? ( 
              <p className="error-text">
                <span className="font-medium">
                  {errors["organization_id"]?.message}
                </span>
              </p> )
            : null}
          </div>
          <div className="sm:col-span-2">
            <Label 
              required
              id="email" 
              name="Email"
              hint="It is used to log in to ActiUp Workspace, the password will be instructed to set up via email after successfully creating an account."
            />
            <Input 
              id="email"
              type="text"
              placeholder="Email"
              error={errors["email"]}
              {...register("email")}
            />
            {errors["email"] ? ( 
              <p className="error-text">
                <span className="font-medium">
                  {errors?.email.message}
                </span>
              </p> 
            ): null}
          </div>
          <div className="sm:col-span-2">
            <Label 
              required
              id="first_name" 
              name="First name"
            />
            <Input 
              id="first_name"
              type="text"
              placeholder="First name"
              maxLength={50}
              error={errors["first_name"]}
              {...register("first_name")}
            />
            <div className="flex justify-between flex-row-reverse">
              <p className="text-xs leading-6">{(watch("first_name") ?? "").length}/50</p>
                {errors["first_name"] ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["first_name"].message}
                  </span>
                </p> 
              ): null}
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label 
              required
              id="last_name" 
              name="Last name"
            />
            <Input 
              id="last_name"
              type="text"
              placeholder="Last name"
              maxLength={50}
              error={errors["last_name"]}
              {...register("last_name")}
            />
            <div className="flex justify-between flex-row-reverse">
              <p className="text-xs leading-6">{(watch("last_name") ?? "").length}/50</p>
                {errors["last_name"] ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["last_name"].message}
                  </span>
                </p> 
              ): null}
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label 
              required
              id="role_id" 
              name="Role name"
            />
            <Controller
              control={control}
              name="role_id"
              defaultValue=""
              render={({ field: { onChange, value = "", ref } }) => (
                <Select 
                  key={`role_id__${watch("organization_id")}`}
                  inputRef={ref}
                  options={organizationRolesIds}
                  id="role_id"
                  placeholder="Select Role"
                  value={organizationRolesIds.find((selected: {label: string; value: string}) => selected.value === value)}
                  onChange={(selected: {label: string; value: string}) => onChange(selected?.value)}
                  error={errors["role_id"]}
                  isLoading={isFetchingOrganizationRoles}
                />
              )}
            />
            {errors["role_id"] ? ( 
              <p className="error-text">
                <span className="font-medium">
                  {errors["role_id"]?.message}
                </span>
              </p> )
            : null}
          </div>
          <div className="sm:col-span-4">
            <Label 
              id="contact_phone" 
              name="Contact phone"
            />
            <div className="flex">
              <Controller
                control={control}
                name="phone_area_code"
                defaultValue="84"
                render={({ field: { onChange, value = "", ref } }) => {
                  const areaPhoneCodeArr = PHONE_CODE.map(item => ({label: item.label, value: item.code}));
                  return (
                    <Select 
                      ref={ref}
                      className="flex-1 w-[160px] !rounded-none inline-flex items-center !rounded-l-md"
                      options={areaPhoneCodeArr} 
                      id="phone_area_code"
                      value={areaPhoneCodeArr.find(c => c.value === value)}
                      onChange={(selected: {label: string; value: string;}) => onChange(selected?.value)}
                      error={errors["phone_area_code"]}
                    />
                  )
                }}
              />
              <Input
                id="contact_phone"
                className={`${errors["phone"] ? "border-l-[1px] focus:!ml-0" : "border-l-0"} rounded-l-none focus:border-l-[1px] focus:ml-[-1px] z-[2]`}
                placeholder="Phone number"
                error={errors["phone"]}
                {...register("phone")}
              />
            </div>
            {errors["phone"] ? ( 
              <p className="error-text">
                <span className="font-medium">
                  {errors["phone"]?.message}
                </span>
              </p> )
            : null}
          </div>
          <div className="sm:col-span-6">
            <Label 
              id="events" 
              name="Events"  
            />
            <Controller
              control={control}
              name="event_ids"
              defaultValue={[]}
              render={({ field: { onChange, value = [], ref } }) => (
                <Select 
                  isMulti
                  key={`event_ids__${watch("organization_id")}`}
                  inputRef={ref}
                  options={value.includes("all") ? [] : organizationEventIds}
                  id="event_ids"
                  placeholder="Select Event"
                  value={value.map((id: string) => {
                    return organizationEventIds.find(item => item.value === id)
                  })}
                  onChange={(selected: {label: string; value: string}[]) => selected.find(item => item.value === EVENT_ALL_OPTION.value) ? onChange([EVENT_ALL_OPTION.value]) : onChange(selected.map(item => `${item.value}`))}
                  error={errors["event_ids"]}
                  isLoading={isFetchingOrganizationEvents}
                />
              )}
            />
            {errors["event_ids"] ? ( 
              <p className="error-text">
                <span className="font-medium">
                  {errors["event_ids"]?.message}
                </span>
              </p> )
            : null}
          </div>
        </div>
      </div>
      <div className="w-full px-4 py-4 sm:px-6 bg-white">
        <div className="flex items-center justify-end gap-x-4">
          <Link to={"/organization/accounts"}>
            <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
              CANCEL
            </Button>
          </Link>
          <Button className="w-20" type="submit">
            SAVE
          </Button>
        </div>
      </div>
      {isLoading ? <FullScreenLoading /> : <></>}
    </form>
  )
} 